import React, { useEffect, useState } from "react";
import { Button, FormControlLabel, Box, Card, Typography } from "@mui/material";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import { Checkbox, Drawer, Select, TextField, toast } from "@outpave/ds";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { useFormik } from "formik";
import { fieldErrorText } from "utils/formikFieldError";
import PaveBalanceIcon from "../../icon/PaveBalanceIcon";
import { ReactComponent as Trophy } from "../../../assets/images/Trophy.svg";
import BankIcon from "../../icon/BankIcon";
import Confetti from "react-confetti";
import LoadingWithoutModal from "../../common/LoadingWithoutModal";
import CurrencyIcon from "../../icon/CurrencyIcon";
import rewards from "../../../validations/rewards";
import { formatCashFull } from "../../../utils/formatCash";
import { useMutation, useQuery } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import { REDEEM_REWARDS } from "../../../graphql/account";
import { getUserFacingErrorMessage } from "utils/graphql";
import { GET_LIST_BANK_ACCOUNTS } from "../../../graphql/company";
import BankTermsDialog from "../bank/BankTermsDialog";
import { useAuth } from "@frontegg/react";
import { GET_USER } from "../../../graphql/user";
import useAddHostedBankAccount from "../../../hooks/useHostedBankAccount";
import useAcceptTerms from "../../../hooks/useAcceptTerms";
import TransferTermsAndConditions from "../common/TransferTermsAndConditions";
import AccountType from "enums/AccountType";
import AccountStatus from "enums/AccountStatus";

// The icons ship their own width/height, so the circle resizes them.
const REWARD_OPTION_ICON_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: "var(--radius-full)",
  height: "56px",
  width: "56px",
  "& svg": { width: 32, height: 32 },
};

const REWARD_OPTION_CARD_SX = {
  padding: "var(--space-6)",
  display: "flex",
  flexDirection: "column",
  marginTop: "var(--space-5)",
  marginInline: "var(--space-10)",
};

const TROPHY_CIRCLE_SX = {
  display: "flex",
  backgroundColor: "var(--color-bg-brand-subtle)",
  borderRadius: "var(--radius-full)",
  width: "96px",
  height: "96px",
  justifyContent: "center",
  alignItems: "center",
  paddingBottom: "var(--space-2)",
};

interface TransferRewardsDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
  handleRefreshAllData: () => void;
  availableRewardsBalance: {
    amount: number;
    currency: string;
  };
}

interface BankAccount {
  id: string;
  bankName: string;
  last4: string;
}

type RewardsAccountFormikProps = {
  fundingAccount: string;
  fundingAccountName: string;
  amount: string;
  earnedRewards: string;
  memo: string;
  confirmTransfer: boolean;
};

enum CurrentScreenChoices {
  Home = "home",
  Redeemed = "redeemed",
  Transfer = "transfer",
}

enum TransferRewardType {
  OutpaveBalance = "outpave_balance",
  BankTransfer = "bank_transfer",
  NotChosen = "NotChosen",
}

const TransferRewardsDrawer: React.FC<TransferRewardsDrawerProps> = (props) => {
  const {
    open,
    handleCloseDrawer,
    availableRewardsBalance,
    handleRefreshAllData,
  } = props;
  const { t } = useTranslation();

  const [currentScreen, setCurrentScreen] = useState<CurrentScreenChoices>(
    CurrentScreenChoices.Home,
  );
  const [transferRewardType, setTransferRewardType] =
    useState<TransferRewardType>(TransferRewardType.NotChosen);
  // One id per redemption flow: a retry after a lost response is the same payment, a new flow is a new one
  const [requestId, setRequestId] = useState<string>(() => uuidv4());

  const handleCloseRewards = () => {
    setCurrentScreen(CurrentScreenChoices.Home);
    formikRewards.resetForm();
    handleCloseDrawer();
  };

  const handleOpenTransferDrawer = (transferType: TransferRewardType) => {
    transferType === TransferRewardType.BankTransfer
      ? setTransferRewardType(TransferRewardType.BankTransfer)
      : setTransferRewardType(TransferRewardType.OutpaveBalance);
    setRequestId(uuidv4());
    setCurrentScreen(CurrentScreenChoices.Transfer);
  };

  const handleOpenRedeemedDrawer = () => {
    setCurrentScreen(CurrentScreenChoices.Redeemed);
  };

  const handleBackButton = () => {
    setCurrentScreen(CurrentScreenChoices.Home);
  };

  const formikRewards = useFormik<RewardsAccountFormikProps>({
    initialValues: {
      fundingAccount: "",
      fundingAccountName: "",
      amount: "",
      earnedRewards: "",
      memo: "",
      confirmTransfer: false,
    },
    validationSchema: rewards,
    onSubmit: (values) => {
      const { fundingAccount, amount, memo } = values;
      const toBank = transferRewardType === TransferRewardType.BankTransfer;

      redeemRewards({
        variables: {
          input: {
            requestId,
            destination: toBank ? "BANK" : "BALANCE",
            amount: parseFloat(amount),
            ...(toBank && { toAccountId: fundingAccount, memo }),
          },
        },
      });
    },
  });

  const [redeemRewards, { loading: redeeming }] = useMutation(REDEEM_REWARDS, {
    onCompleted: () => {
      onRewardAddSuccess();
    },
    onError: (error) => {
      toast.error(
        getUserFacingErrorMessage(
          error,
          t("SomethingWentWrongDescriptionWithSupport"),
        ),
      );
    },
  });

  const onRewardAddSuccess = () => {
    toast.success(t("Rewards claimed!"));
    handleOpenRedeemedDrawer();
    handleRefreshAllData();
  };

  const earnedRewards = availableRewardsBalance.amount;
  const earnedRewardsDisplay = formatCashFull(
    availableRewardsBalance.amount,
    availableRewardsBalance.currency,
  );

  useEffect(() => {
    if (open) {
      formikRewards.setFieldValue("earnedRewards", earnedRewards);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earnedRewards, open]);

  const busy = redeeming;

  return (
    // No title: this panel never had a header, so the DS banner carries the
    // close button alone and `ariaLabel` is what names the dialog.
    <Drawer
      open={open}
      onClose={() => {
        if (busy) return;
        handleCloseRewards();
      }}
      ariaLabel={t("Rewards")}
      loading={busy}
      footer={
        <>
          {currentScreen !== CurrentScreenChoices.Redeemed && (
            <Button
              variant="primary-outlined"
              onClick={() => {
                handleCloseRewards();
              }}
            >
              {t("Close")}
            </Button>
          )}
          {currentScreen === CurrentScreenChoices.Transfer && (
            <Button
              variant="primary-outlined"
              onClick={() => {
                handleBackButton();
              }}
            >
              {t("Back")}
            </Button>
          )}
        </>
      }
    >
      {currentScreen === CurrentScreenChoices.Home && (
        <EarnedRewards
          earnedRewardsDisplay={earnedRewardsDisplay}
          onClickRedeem={() => {
            handleOpenTransferDrawer(TransferRewardType.OutpaveBalance);
          }}
          onClickTransfer={() => {
            handleOpenTransferDrawer(TransferRewardType.BankTransfer);
          }}
        />
      )}

      {currentScreen === CurrentScreenChoices.Redeemed && (
        <RedeemedRewards
          transferRewardType={transferRewardType}
          formikRewards={formikRewards}
          onClickRedeemed={() => {
            handleCloseRewards();
          }}
        />
      )}

      {currentScreen === CurrentScreenChoices.Transfer && (
        <TransferRewards
          earnedRewardsDisplay={earnedRewardsDisplay}
          transferRewardType={transferRewardType}
          formikRewards={formikRewards}
          loadingMoneyIntoAcount={busy}
        />
      )}
    </Drawer>
  );
};

export default TransferRewardsDrawer;

////////////////////////////////////
////// EARNED REWARDS (home) ///////
////////////////////////////////////

interface EarnedRewardsProps {
  onClickRedeem: () => void;
  onClickTransfer: () => void;
  earnedRewardsDisplay: string;
}

const EarnedRewards: React.FC<EarnedRewardsProps> = (props) => {
  const { onClickRedeem, onClickTransfer, earnedRewardsDisplay } = props;
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="body1" sx={{ textAlign: "center" }}>
        {t("EARNED REWARDS")}
      </Typography>
      <Typography
        variant="h3"
        sx={{
          textAlign: "center",
          marginTop: "var(--space-7)",
          marginInline: "var(--space-12)",
        }}
      >
        {t("How would you like to redeem your rewards?")}
      </Typography>
      <Typography
        variant="h1"
        sx={{
          textAlign: "center",
          marginTop: "var(--space-3)",
          marginBottom: "var(--space-10)",
        }}
      >
        {earnedRewardsDisplay}
      </Typography>

      <Card variant="outlined" sx={REWARD_OPTION_CARD_SX}>
        <Inline wrap={false} justify="between" gap="0">
          <Stack gap="0" sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h4">{t("Available Balance")}</Typography>
            <Typography
              variant="body1"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("Add to account (Available immediately)")}
            </Typography>
          </Stack>
          <Box sx={REWARD_OPTION_ICON_SX}>
            <PaveBalanceIcon />
          </Box>
        </Inline>
        <Inline
          wrap={false}
          justify="center"
          gap="0"
          sx={{ width: "100%", marginTop: "var(--space-7)" }}
        >
          <Button
            sx={{ width: "366px" }}
            variant="primary-filled"
            size="large"
            onClick={onClickRedeem}
          >
            {t("Redeem")}
          </Button>
        </Inline>
      </Card>

      <Card variant="outlined" sx={REWARD_OPTION_CARD_SX}>
        <Inline wrap={false} justify="between" gap="0">
          <Stack gap="0" sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h4">{t("Bank Transfer")}</Typography>
            <Typography
              variant="body1"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("Send to external bank (3 business days)")}
            </Typography>
          </Stack>
          <Box sx={REWARD_OPTION_ICON_SX}>
            <BankIcon />
          </Box>
        </Inline>
        <Inline
          wrap={false}
          justify="center"
          gap="0"
          sx={{ width: "100%", marginTop: "var(--space-7)" }}
        >
          <Button
            sx={{ width: "366px" }}
            variant="primary-outlined"
            size="large"
            onClick={onClickTransfer}
          >
            Transfer
          </Button>
        </Inline>
      </Card>
    </>
  );
};

////////////////////////////////////
///////// TRANSFER REWARDS /////////
////////////////////////////////////

interface TransferRewardProps {
  earnedRewardsDisplay: string;
  transferRewardType: string;
  formikRewards: any;
  loadingMoneyIntoAcount: boolean;
}

const TransferRewards: React.FC<TransferRewardProps> = (props) => {
  const {
    earnedRewardsDisplay,
    formikRewards,
    transferRewardType,
    loadingMoneyIntoAcount,
  } = props;
  const { t } = useTranslation();
  const { user } = useAuth();

  const [openTerms, setOpenTerms] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [accountKey, setAccountKey] = useState<string>("");
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount>({
    id: "",
    bankName: "",
    last4: "",
  });

  const { data: userData, loading: loadingUser } = useQuery(GET_USER, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  const [variables, setVariables] = useState({
    status: [AccountStatus.ACTIVE],
    types: [AccountType.BANK, AccountType.REWARDS_BANK],
    refresh: 0,
  });

  const { loading: loadingBankAccounts } = useQuery(GET_LIST_BANK_ACCOUNTS, {
    variables: variables,
    skip: transferRewardType === TransferRewardType.OutpaveBalance,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setBankAccounts(data.getListBankAccounts);

      if (paymentMethod) {
        setSelectedAccount(
          data.getListBankAccounts.find(
            (account: BankAccount) => account.id === paymentMethod,
          ),
        );
      } else if (data?.getListBankAccounts) {
        if (data.getListBankAccounts.length > 0) {
          setSelectedAccount(data.getListBankAccounts[0]);
        }
      }
    },
  });

  const { addHostedBankAccount, loadingBank } = useAddHostedBankAccount(
    userData,
    setClientSecret,
    setAccountKey,
    setPaymentMethod,
    setOpenTerms,
    setPublishableKey,
  );

  const handleCloseAddBank = (isSuccess: boolean) => {
    setOpenTerms(false);
    if (isSuccess) {
      setVariables({
        status: [AccountStatus.ACTIVE],
        types: [AccountType.BANK, AccountType.REWARDS_BANK],
        refresh: Math.random(),
      });
    }
  };

  const { handleAcceptTerms } = useAcceptTerms(
    accountKey,
    clientSecret,
    handleCloseAddBank,
    AccountType.BANK,
    publishableKey,
  );

  async function handleOpenAddBank() {
    await addHostedBankAccount();
  }

  useEffect(() => {
    if (transferRewardType === TransferRewardType.BankTransfer) {
      formikRewards.setFieldValue("fundingAccount", selectedAccount.id);
      formikRewards.setFieldValue(
        "fundingAccountName",
        selectedAccount.bankName,
      );
    }
    if (transferRewardType === TransferRewardType.OutpaveBalance) {
      formikRewards.setFieldValue("fundingAccount", "outpave");
      formikRewards.setFieldValue("fundingAccountName", "Account Balance");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, transferRewardType]);

  return (
    <>
      <Typography variant="body1" sx={{ textAlign: "center" }}>
        {t("REDEEM REWARDS")}
      </Typography>
      <Inline
        wrap={false}
        justify="center"
        gap="0"
        sx={{ marginTop: "var(--space-7)" }}
      >
        {transferRewardType === TransferRewardType.OutpaveBalance ? (
          <Trophy />
        ) : (
          <Box sx={TROPHY_CIRCLE_SX}>
            <BankIcon />
          </Box>
        )}
      </Inline>
      <Typography
        variant="h1"
        sx={{ textAlign: "center", marginTop: "var(--space-7)" }}
      >
        {earnedRewardsDisplay}
      </Typography>
      <Typography
        variant="h3"
        sx={{ textAlign: "center", marginTop: "var(--space-5)" }}
      >
        {transferRewardType === TransferRewardType.OutpaveBalance
          ? t("Transfer rewards to Outpave")
          : t("Transfer rewards to bank")}
      </Typography>
      {transferRewardType === TransferRewardType.BankTransfer && (
        <Inline
          wrap={false}
          justify="center"
          gap="0"
          sx={{ marginTop: "var(--space-10)" }}
        >
          <Box sx={{ width: "400px" }}>
            <Select
              label={t("Select Bank")}
              fullWidth
              clearable={false}
              loading={loadingBankAccounts}
              value={formikRewards.values.fundingAccount || null}
              onChange={(id) => {
                const account = bankAccounts.find(
                  (bankAccount) => bankAccount.id === id,
                );
                if (account) {
                  setSelectedAccount(account);
                }
              }}
              options={bankAccounts.map((bankAccount) => ({
                id: bankAccount.id,
                label: `${bankAccount.bankName} **** ${bankAccount.last4}`,
              }))}
              bottomAction={{
                label: t("Add new account"),
                icon: <AddCircleOutlineIcon sx={{ fontSize: "16px" }} />,
                onClick: handleOpenAddBank,
              }}
            />
          </Box>
        </Inline>
      )}

      <Inline
        wrap={false}
        justify="center"
        gap="0"
        sx={{ marginTop: "var(--space-7)" }}
      >
        <Box sx={{ width: "400px" }}>
          <TextField
            label="Enter Amount"
            fullWidth
            name="amount"
            type="number"
            onBlur={formikRewards.handleBlur}
            value={formikRewards.values.amount}
            onChange={formikRewards.handleChange}
            errorText={fieldErrorText(formikRewards, "amount")}
            leading={<CurrencyIcon />}
            inputProps={{
              onWheel: (event) => event.currentTarget.blur(), // Disable scroll wheel increment/decrement
            }}
          />
        </Box>
      </Inline>

      {transferRewardType === TransferRewardType.BankTransfer && (
        <Inline
          wrap={false}
          justify="center"
          gap="0"
          sx={{ marginTop: "var(--space-7)" }}
        >
          <Box sx={{ width: "400px" }}>
            <TextField
              label="Memo"
              fullWidth
              name="memo"
              type="text"
              multiline
              minRows={1}
              maxRows={10}
              onBlur={formikRewards.handleBlur}
              value={formikRewards.values.memo}
              onChange={formikRewards.handleChange}
              errorText={fieldErrorText(formikRewards, "memo")}
            />
          </Box>
        </Inline>
      )}

      <Box sx={{ paddingTop: "var(--space-9)" }}>
        <FormControlLabel
          name="confirmTransfer"
          onChange={formikRewards.handleChange}
          control={
            <Checkbox
              checked={formikRewards.values["confirmTransfer"]}
              disableRipple
            />
          }
          label={
            <Typography component="span" variant="h5">
              {t("Confirm Transfer")}
            </Typography>
          }
        />
        <Box
          sx={{
            display: "block",
            paddingBlock: "var(--space-5)",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <TransferTermsAndConditions />
        </Box>
      </Box>

      <Inline
        wrap={false}
        justify="center"
        gap="0"
        sx={{ marginTop: "var(--space-10)" }}
      >
        <Button
          sx={{ width: "225px" }}
          variant="primary-filled"
          size="large"
          onClick={formikRewards.handleSubmit}
          disabled={
            !formikRewards.isValid ||
            !formikRewards.values.confirmTransfer ||
            loadingMoneyIntoAcount
          }
        >
          {t("Transfer $")}
          {formikRewards.value?.amount}
        </Button>
      </Inline>

      <BankTermsDialog
        open={openTerms}
        onClose={(accepted: boolean) => {
          setOpenTerms(false);
          if (accepted) {
            handleAcceptTerms();
          } else {
            handleCloseAddBank(false);
          }
        }}
      />

      <LoadingWithoutModal
        open={loadingMoneyIntoAcount || loadingBank || loadingUser}
      />
    </>
  );
};

interface RedeemedRewardsProps {
  transferRewardType: string;
  formikRewards: any;
  onClickRedeemed: () => void;
}

////////////////////////////////////
///// REDEEMED REWARDS (final) /////
////////////////////////////////////

const RedeemedRewards: React.FC<RedeemedRewardsProps> = (props) => {
  const { transferRewardType, formikRewards, onClickRedeemed } = props;
  const { t } = useTranslation();
  return (
    <>
      <Typography variant="body1" sx={{ textAlign: "center" }}>
        {" "}
        {t("REDEEMED REWARDS")}
      </Typography>
      <Inline
        wrap={false}
        justify="center"
        gap="0"
        sx={{ marginTop: "var(--space-7)" }}
      >
        {transferRewardType === TransferRewardType.OutpaveBalance ? (
          <Trophy />
        ) : (
          <Box sx={TROPHY_CIRCLE_SX}>
            <BankIcon />
          </Box>
        )}
      </Inline>
      <Confetti />
      <Typography
        variant="h1"
        sx={{
          textAlign: "center",
          marginTop: "var(--space-5)",
          marginBottom: "var(--space-10)",
        }}
      >
        {formatCashFull(formikRewards.values.amount, "USD")}
      </Typography>
      {transferRewardType === TransferRewardType.OutpaveBalance ? (
        <Typography
          variant="h3"
          sx={{ textAlign: "center", marginTop: "var(--space-7)" }}
        >
          {t("Rewards redeemed and added to Outpave Balance")}
        </Typography>
      ) : (
        <Typography
          variant="h3"
          sx={{ textAlign: "center", marginTop: "var(--space-7)" }}
        >
          {t("Rewards will arrive at ")}
          {formikRewards.values.fundingAccountName} {t(" in 3 business days")}
        </Typography>
      )}
      <Typography
        variant="body1"
        sx={{
          textAlign: "center",
          marginTop: "var(--space-7)",
          marginBottom: "var(--space-11)",
          marginInline: "var(--space-12)",
        }}
      >
        {t("Your smart spending with Outpave is making a difference!")}
      </Typography>
      <Inline wrap={false} justify="center" gap="0">
        <Button
          sx={{ width: "225px" }}
          variant="primary-filled"
          size="large"
          onClick={onClickRedeemed}
        >
          {t("Back to home")}
        </Button>
      </Inline>
    </>
  );
};
